import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import api from '../services/api';
import SessionDetailsModal from './SessionDetailsModal';
import AuditSummaryModal from './AuditSummaryModal';

const AuditTab = () => {
    const [sessions, setSessions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedSessionId, setSelectedSessionId] = useState(null);
    const [pagination, setPagination] = useState({
        total: 0,
        page: 1,
        pageSize: 20,
        pages: 1
    });
    const [showSummary, setShowSummary] = useState(false);

    // Fix: Use standard Colombia date string per SKILL.md
    const getColombiaDate = () => {
        return new Date().toLocaleDateString('en-CA', { timeZone: 'America/Bogota' });
    };

    const getSevenDaysAgo = () => {
        const date = new Date(getColombiaDate());
        date.setDate(date.getDate() - 7);
        return date.toLocaleDateString('en-CA', { timeZone: 'America/Bogota' });
    }

    // Filters
    const [dateRange, setDateRange] = useState({
        from: '2026-01-01', 
        to: getColombiaDate()
    });

    useEffect(() => {
        // Reset to page 1 when dates change to avoid "empty page" issues
        if (pagination.page !== 1) {
            setPagination(prev => ({ ...prev, page: 1 }));
        } else {
            fetchSessions(1);
        }
    }, [dateRange]);

    useEffect(() => {
        fetchSessions(pagination.page);
    }, [pagination.page]);

    // Deep linking support for report
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        if (params.get('report') === 'true') {
            const from = params.get('from');
            const to = params.get('to');
            if (from && to) {
                setDateRange({ from, to });
                setShowSummary(true);
            }
        }
    }, []);

    const fetchSessions = async (pageNumber = 1) => {
        try {
            setLoading(true);
            const response = await api.get('/cash-box/sessions', {
                params: {
                    from_date: dateRange.from,
                    to_date: dateRange.to,
                    page: pageNumber,
                    pageSize: pagination.pageSize
                }
            });
            setSessions(response.data.sessions || []);
            if (response.data.pagination) {
                setPagination(response.data.pagination);
            }
        } catch (error) {
            console.error('Error fetching sessions:', error);
        } finally {
            setLoading(false);
        }
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('es-CO', {
            style: 'currency',
            currency: 'COP',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(Math.round(amount || 0));
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleString('es-CO');
    };

    const getStatusBadge = (status) => {
        const styles = {
            open: 'bg-green-100 text-green-800',
            closed: 'bg-gray-100 text-gray-800',
            reconciled: 'bg-blue-100 text-blue-800'
        };
        const labels = {
            open: 'ABIERTA',
            closed: 'CERRADA',
            reconciled: 'AUDITADA'
        };
        return (
            <span className={`px-2 py-1 rounded-full text-xs font-bold ${styles[status] || 'bg-gray-100'}`}>
                {labels[status] || status}
            </span>
        );
    };

    return (
        <div className="space-y-6">
            {/* Filters */}
            <Card>
                <CardContent className="pt-6">
                    <div className="flex flex-wrap gap-4 items-end">
                        <div className="flex flex-col gap-1">
                            <label className="text-sm font-medium text-gray-600">Desde</label>
                            <input
                                type="date"
                                value={dateRange.from}
                                onChange={(e) => setDateRange(prev => ({ ...prev, from: e.target.value }))}
                                className="border rounded px-3 py-2"
                            />
                        </div>
                        <div className="flex flex-col gap-1">
                            <label className="text-sm font-medium text-gray-600">Hasta</label>
                            <input
                                type="date"
                                value={dateRange.to}
                                onChange={(e) => setDateRange(prev => ({ ...prev, to: e.target.value }))}
                                className="border rounded px-3 py-2"
                            />
                        </div>
                        <button
                            onClick={fetchSessions}
                            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 font-semibold"
                        >
                            Filtrar
                        </button>
                        <button
                            onClick={() => {
                                setDateRange({ from: '2026-01-01', to: getColombiaDate() });
                                setPagination(prev => ({ ...prev, page: 1 }));
                            }}
                            className="bg-gray-100 text-gray-600 px-4 py-2 rounded hover:bg-gray-200 border border-gray-300 font-medium"
                        >
                            Ver Todo
                        </button>
                        <button
                            onClick={() => setShowSummary(true)}
                            className="bg-orange-100 text-orange-700 px-4 py-2 rounded hover:bg-orange-200 border border-orange-200 font-bold ml-auto"
                        >
                            📊 Generar Informe
                        </button>
                    </div>
                </CardContent>
            </Card>

            {/* Sessions Table */}
            <Card>
                <CardHeader>
                    <CardTitle>📜 Historial de Sesiones</CardTitle>
                </CardHeader>
                <CardContent>
                    {loading ? (
                        <div className="text-center py-8">Cargando historial...</div>
                                        ) : sessions.length > 0 ? (
                        <>
                            <div className="overflow-x-auto">
                                <table className="w-full text-left border-collapse">
                                    <thead>
                                        <tr className="border-b bg-gray-50 text-gray-600 text-sm">
                                            <th className="p-3">Caja #</th>
                                            <th className="p-3">Apertura</th>
                                            <th className="p-3">Cierre</th>
                                            <th className="p-3">Responsable</th>
                                            <th className="p-3 text-right">Base Inicial</th>
                                            <th className="p-3 text-right">Saldo Declarado</th>
                                            <th className="p-3 text-right">Diferencia</th>
                                            <th className="p-3 text-center">Estado</th>
                                            <th className="p-3 text-center">Acciones</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y">
                                        {sessions.map(session => (
                                            <tr key={session.id} className="hover:bg-gray-50 transition-colors">
                                                <td className="p-3 font-bold text-sm">#{session.session_number || session.id}</td>
                                                <td className="p-3 text-sm">{formatDate(session.opened_at)}</td>
                                                <td className="p-3 text-sm">{formatDate(session.closed_at)}</td>
                                                <td className="p-3 text-sm">{session.opened_by_name}</td>
                                                <td className="p-3 text-right font-mono text-sm">
                                                    {formatCurrency(session.opening_balance)}
                                                </td>
                                                <td className="p-3 text-right font-mono text-sm">
                                                    {session.status === 'closed' ? formatCurrency(session.closing_balance_declared) : '-'}
                                                </td>
                                                <td className={`p-3 text-right font-bold text-sm ${parseFloat(session.difference) === 0 ? 'text-gray-400' :
                                                    Math.abs(parseFloat(session.difference)) < 5000 ? 'text-green-600' :
                                                    parseFloat(session.difference) > 0 ? 'text-green-600' : 'text-red-600'
                                                    }`}>
                                                    {session.status === 'closed' ? formatCurrency(session.difference) : '-'}
                                                </td>
                                                <td className="p-3 text-center">
                                                    {getStatusBadge(session.status)}
                                                </td>
                                                <td className="p-3 text-center">
                                                    <button
                                                        onClick={() => setSelectedSessionId(session.id)}
                                                        className="text-blue-600 hover:text-blue-800 underline text-sm"
                                                    >
                                                        Ver Detalle
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            {/* Pagination Controls */}
                            {pagination.pages > 1 && (
                                <div className="mt-4 flex items-center justify-between border-t pt-4">
                                    <div className="text-sm text-gray-500">
                                        Mostrando {(pagination.page - 1) * pagination.pageSize + 1} a {Math.min(pagination.page * pagination.pageSize, pagination.total)} de {pagination.total} sesiones
                                    </div>
                                    <div className="flex gap-2">
                                        <button
                                            disabled={pagination.page <= 1}
                                            onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                                            className="px-3 py-1 border rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            Anterior
                                        </button>
                                        <div className="flex gap-1">
                                            {[...Array(pagination.pages)].map((_, i) => {
                                                const pageNum = i + 1;
                                                if (
                                                    pagination.pages > 10 &&
                                                    Math.abs(pageNum - pagination.page) > 2 &&
                                                    pageNum !== 1 &&
                                                    pageNum !== pagination.pages
                                                ) {
                                                    if (Math.abs(pageNum - pagination.page) === 3) return <span key={pageNum} className="px-1 items-center flex">...</span>;
                                                    return null;
                                                }

                                                return (
                                                    <button
                                                        key={pageNum}
                                                        onClick={() => setPagination(prev => ({ ...prev, page: pageNum }))}
                                                        className={`px-3 py-1 border rounded transition-colors ${pagination.page === pageNum
                                                            ? 'bg-blue-600 text-white border-blue-600'
                                                            : 'hover:bg-gray-50'
                                                            }`}
                                                    >
                                                        {pageNum}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                        <button
                                            disabled={pagination.page >= pagination.pages}
                                            onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                                            className="px-3 py-1 border rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            Siguiente
                                        </button>
                                    </div>
                                </div>
                            )}
                        </>
                    ) : (
                        <div className="text-center py-10 text-gray-500">
                            No se encontraron sesiones en el rango seleccionado
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Details Modal */}
            {selectedSessionId && (
                <SessionDetailsModal
                    sessionId={selectedSessionId}
                    onClose={() => setSelectedSessionId(null)}
                />
            )}

            {/* Audit Summary Modal */}
            {showSummary && (
                <AuditSummaryModal
                    dateRange={dateRange}
                    onClose={() => setShowSummary(false)}
                />
            )}
        </div>
    );
};

export default AuditTab;
